import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { createSelector } from "reselect";
import { withTranslation } from "react-i18next";
import Select from "react-select";
import { Container, Row, Col, Button } from "reactstrap";
import BreadcrumbLink from "../../components/Common/BreadcrumbLink";
import AlertError from "../../components/Alerts/AlertError";
import AlertSuccess from "../../components/Alerts/AlertSuccess";
import ConfigSection from "./ConfigSection";
import ToggleSwitch from "./ToggleSwitch";
import { orgId } from "../../constants/utils";
import {
  getRiderCheckConfig,
  putRiderCheckConfig,
  getOrgsAdmin,
} from "../../helpers/api_rombo_helper";

const RiderCheckConfigPage = ({ t }) => {
  const navigate = useNavigate();

  const LoginProperties = createSelector(
    (state) => state.Login,
    (Login) => ({ token: Login.token, loading: Login.loading, data: Login.data })
  );
  const { token, loading, data } = useSelector(LoginProperties);

  const isStaff = data?.data?.is_staff === true;
  const profileResolved = !loading && !!data?.data;

  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [companyFilter, setCompanyFilter] = useState(null);
  const [savingIds, setSavingIds] = useState(() => new Set());
  const [showAlertError, setShowAlertError] = useState(false);
  const [alertBody, setAlertBody] = useState("");
  const [showAlertSuccess, setShowAlertSuccess] = useState(false);
  const [successBody, setSuccessBody] = useState("");

  useEffect(() => {
    if (profileResolved && !isStaff) {
      navigate("/dashboard", { replace: true });
    }
  }, [profileResolved, isStaff, navigate]);

  useEffect(() => {
    document.title = `${t("ConfigurationTitle")} - ${orgId}`;
  }, [t]);

  const mergeConfigWithPartners = (config, partners) => {
    const partnerById = new Map();
    (partners || []).forEach((p) => {
      if (p?.partner_id != null) partnerById.set(p.partner_id, p);
    });
    return (config || []).map((cfg) => {
      const partner = partnerById.get(cfg.partner_id) || {};
      return {
        partner_id: cfg.partner_id,
        name: cfg.name ?? partner.name ?? null,
        alias_name: cfg.alias_name ?? partner.alias_name ?? null,
        is_active: cfg.is_active ?? partner.is_active ?? true,
        checkin_enabled: !!cfg.checkin_enabled,
        checkout_signature_enabled: !!cfg.checkout_signature_enabled,
      };
    });
  };

  const fetchData = useCallback(async () => {
    if (!token) return;
    setIsLoading(true);
    setLoadError(false);
    try {
      const [config, partners] = await Promise.all([
        getRiderCheckConfig(token),
        getOrgsAdmin(token).catch(() => []),
      ]);
      const merged = mergeConfigWithPartners(
        Array.isArray(config) ? config : [],
        Array.isArray(partners) ? partners : []
      );
      merged.sort((a, b) => {
        const an = (a.alias_name || a.name || "").toLowerCase();
        const bn = (b.alias_name || b.name || "").toLowerCase();
        return an.localeCompare(bn);
      });
      setRows(merged);
    } catch (e) {
      setLoadError(true);
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (isStaff) fetchData();
  }, [isStaff, fetchData]);

  const setSaving = (partnerId, saving) => {
    setSavingIds((prev) => {
      const next = new Set(prev);
      if (saving) next.add(partnerId);
      else next.delete(partnerId);
      return next;
    });
  };

  const showError = (message) => {
    setAlertBody(message || t("RiderCheckConfigSaveError"));
    setShowAlertError(true);
  };

  const patchRow = (partnerId, patch) => {
    setRows((prev) => prev.map((r) => (r.partner_id === partnerId ? { ...r, ...patch } : r)));
  };

  const showSuccess = (message) => {
    setSuccessBody(message);
    setShowAlertSuccess(true);
  };

  // `successMessage` describe en palabras el cambio realizado (empresa + opción);
  // se muestra con AlertSuccess solo cuando el PUT confirma el cambio.
  const applyChange = async (row, nextCheckin, nextSignature, successMessage) => {
    const partnerId = row.partner_id;
    const previous = {
      checkin_enabled: row.checkin_enabled,
      checkout_signature_enabled: row.checkout_signature_enabled,
    };
    patchRow(partnerId, {
      checkin_enabled: nextCheckin,
      checkout_signature_enabled: nextSignature,
    });
    setSaving(partnerId, true);
    try {
      const updated = await putRiderCheckConfig(token, partnerId, {
        checkin_enabled: nextCheckin,
        checkout_signature_enabled: nextSignature,
      });
      patchRow(partnerId, {
        checkin_enabled: !!updated?.checkin_enabled,
        checkout_signature_enabled: !!updated?.checkout_signature_enabled,
        ...(updated?.name != null ? { name: updated.name } : {}),
        ...(updated?.alias_name != null ? { alias_name: updated.alias_name } : {}),
        ...(updated?.is_active != null ? { is_active: updated.is_active } : {}),
      });
      if (successMessage) showSuccess(successMessage);
    } catch (e) {
      patchRow(partnerId, previous);
      const status = e?.response?.status;
      if (status === 403) showError(t("RiderCheckConfigForbidden"));
      else if (status === 404) showError(t("RiderCheckConfigNotFound"));
      else showError(e?.response?.data?.detail || t("RiderCheckConfigSaveError"));
    } finally {
      setSaving(partnerId, false);
    }
  };

  // Escapa el nombre para inyectarlo con seguridad en el body del alert
  // (AlertSuccess usa dangerouslySetInnerHTML e i18next no escapa valores).
  const escapeHtml = (str) =>
    String(str)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");

  const companyName = (row) => escapeHtml(row.alias_name || row.name || `#${row.partner_id}`);

  const onToggleCheckin = (row) => {
    const nextCheckin = !row.checkin_enabled;
    const nextSignature = nextCheckin ? row.checkout_signature_enabled : false;
    const message = t(
      nextCheckin ? "RiderCheckConfigCheckinEnabledMsg" : "RiderCheckConfigCheckinDisabledMsg",
      { company: companyName(row) }
    );
    applyChange(row, nextCheckin, nextSignature, message);
  };

  const onToggleSignature = (row) => {
    if (!row.checkin_enabled) return;
    const nextSignature = !row.checkout_signature_enabled;
    const message = t(
      nextSignature
        ? "RiderCheckConfigSignatureEnabledMsg"
        : "RiderCheckConfigSignatureDisabledMsg",
      { company: companyName(row) }
    );
    applyChange(row, true, nextSignature, message);
  };

  const companyOptions = useMemo(() => {
    return rows.map((r) => ({
      label: r.alias_name || r.name || `#${r.partner_id}`,
      value: r.partner_id,
    }));
  }, [rows]);

  const selectedRow = useMemo(
    () => rows.find((r) => r.partner_id === companyFilter) || null,
    [rows, companyFilter]
  );

  if (profileResolved && !isStaff) return null;

  const renderSelectedCompany = (row) => {
    const saving = savingIds.has(row.partner_id);
    const signatureDisabled = saving || !row.checkin_enabled;
    const displayName = row.alias_name || row.name || `#${row.partner_id}`;
    return (
      <div className="config-switch-list">
        <div className="config-switch-row">
          <div className="config-switch-info">
            <span className="config-switch-title">{t("RiderCheckConfigCheckinColumn")}</span>
            <span className="config-switch-hint">{t("RiderCheckConfigCheckinHint")}</span>
          </div>
          <ToggleSwitch
            id={`checkin-${row.partner_id}`}
            checked={row.checkin_enabled}
            disabled={saving}
            onChange={() => onToggleCheckin(row)}
            ariaLabel={t("RiderCheckConfigCheckinColumn")}
          />
        </div>
        <div className="config-switch-row">
          <div className="config-switch-info">
            <span className="config-switch-title">{t("RiderCheckConfigSignatureColumn")}</span>
            <span className="config-switch-hint">{t("RiderCheckConfigSignatureHint")}</span>
          </div>
          <ToggleSwitch
            id={`signature-${row.partner_id}`}
            checked={row.checkout_signature_enabled}
            disabled={signatureDisabled}
            onChange={() => onToggleSignature(row)}
            ariaLabel={t("RiderCheckConfigSignatureColumn")}
          />
        </div>
        {row.is_active === false && (
          <div className="config-switch-inactive">
            <i className="mdi mdi-information-outline" />
            <span>{t("RiderCheckConfigInactiveClientNote")}</span>
          </div>
        )}
      </div>
    );
  };

  const renderBody = () => {
    if (isLoading) {
      return (
        <div className="riders-status-message">
          <div className="spinner-border text-primary" role="status" />
          <p className="mt-2">{t("Loading")}</p>
        </div>
      );
    }
    if (loadError) {
      return (
        <div className="riders-status-message">
          <i className="mdi mdi-alert-circle-outline riders-status-icon" />
          <p>{t("RiderCheckConfigLoadError")}</p>
          <Button color="primary" size="sm" onClick={fetchData}>
            {t("Retry")}
          </Button>
        </div>
      );
    }
    if (!selectedRow) {
      return (
        <div className="riders-status-message">
          <i className="mdi mdi-office-building-outline riders-status-icon" />
          <p>{t("RiderCheckConfigSelectCompanyPrompt")}</p>
        </div>
      );
    }
    return renderSelectedCompany(selectedRow);
  };

  return (
    <div className="page-content">
      <Container fluid>
        <BreadcrumbLink
          title={t("ConfigurationTitle")}
          link="/settings/rider-check"
          breadcrumbItem={t("Passengers")}
        />

        <Row>
          <Col xs={12}>
            <ConfigSection
              icon="bx-id-card"
              title={t("RiderCheckConfigSectionTitle")}
              description={t("RiderCheckConfigSectionDescription")}
            >
              <div className="config-company-block">
                <label className="config-company-label">
                  {t("RiderCheckConfigCompanyLabel")}
                </label>
                <p className="config-company-hint">
                  {t("RiderCheckConfigCompanyHint")}
                </p>
                <Row>
                  <Col xxl={6} md={8} xs={12}>
                    <Select
                      name="company"
                      classNamePrefix="select2-selection"
                      placeholder={t("RiderCheckConfigCompanyPlaceholder")}
                      options={companyOptions}
                      isClearable
                      value={companyOptions.find((o) => o.value === companyFilter) || null}
                      onChange={(opt) => setCompanyFilter(opt?.value ?? null)}
                      noOptionsMessage={() => t("RiderCheckConfigEmpty")}
                      isDisabled={isLoading || loadError}
                    />
                  </Col>
                </Row>
              </div>

              <div className="config-section-divider" />

              {renderBody()}
            </ConfigSection>
          </Col>
        </Row>
        <AlertSuccess
          showAlertSuccess={showAlertSuccess}
          setShowAlertSuccess={setShowAlertSuccess}
          textHeader={t("RiderCheckConfigSaveSuccessTitle")}
          textBody={successBody}
        />

        <AlertError
          showAlertError={showAlertError}
          setShowAlertError={setShowAlertError}
          textHeader={t("RiderCheckConfigSaveErrorTitle")}
          textBody={alertBody}
        />
      </Container>
    </div>
  );
};

export default withTranslation()(RiderCheckConfigPage);
